import React from 'react';
import { Box, Typography, Container, Grid, Chip } from '@mui/material';
import PageBanner from '../components/PageBanner';
import SEO from '../components/SEO';
import SectionSidebar from '../components/SectionSidebar';
import { Link as RouterLink } from 'react-router-dom';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CelebrationIcon from '@mui/icons-material/Celebration';
import GroupsIcon from '@mui/icons-material/Groups';
import GavelIcon from '@mui/icons-material/Gavel';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ForestIcon from '@mui/icons-material/Forest';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import SpaIcon from '@mui/icons-material/Spa';

const GOLD = '#d4af37';

const sections = [
  { title: 'Temple History', desc: 'Discover the divine origin from the Dvapara Yuga, the rediscovery of sacred stones, and centuries of unbroken spiritual heritage.', to: '/about/history', icon: HistoryEduIcon, gradient: ['#8b5cf6', '#7c3aed'] as [string, string] },
  { title: 'Deities', desc: 'Lord Parameshwara, Goddess Parvati, Subrahmanya, Ganesha, and the Sun God — five self-manifested sacred presences.', to: '/about/deities', icon: AutoAwesomeIcon, gradient: ['#f59e0b', '#d97706'] as [string, string] },
  { title: 'Festivals', desc: 'The sacred Manikye Vili (മാണിക്യെ വിളി) ceremony, Aarattu rituals, annual Utsavam, and time-honored festival traditions.', to: '/about/festivals', icon: CelebrationIcon, gradient: ['#ef4444', '#dc2626'] as [string, string] },
  { title: 'Administration', desc: 'From royal custodians to the modern trustee board — governance guided by heritage and community devotion.', to: '/about/administration', icon: GroupsIcon, gradient: ['#3b82f6', '#2563eb'] as [string, string] },
  { title: 'Rules & Timings', desc: 'Visitor guidelines, dress code, worship etiquette, and darshan timings for a sacred temple experience.', to: '/about/rules', icon: GavelIcon, gradient: ['#22c55e', '#16a34a'] as [string, string] },
];

const quickFacts = [
  { label: 'Dvapara Yuga', sub: 'Ancient Sacred Origin', gradient: ['#8b5cf6', '#7c3aed'] },
  { label: '10 Acres', sub: 'of Sacred Forest', gradient: ['#22c55e', '#16a34a'] },
  { label: '5 Deities', sub: 'Self-Manifested Stones', gradient: ['#f59e0b', '#d97706'] },
  { label: 'Parameshwara', sub: 'Principal Deity (Shiva)', gradient: ['#ef4444', '#dc2626'] },
];

export default function About() {
  return (
    <Box sx={{ bgcolor: '#f8fafb', minHeight: '100vh' }}>
      <SEO
        title="About Kottayil Temple Muchukunnu — History & Heritage | കൊട്ടയിൽ ക്ഷേത്രം"
        description="About Kottayil Temple (Kottayil Shiva Temple) Muchukunnu — discover the rich history, heritage, deities, Kottayil Kulam (sacred pond), festivals, and spiritual significance of this ancient temple in Kozhikode, Kerala."
        path="/about"
        keywords="Kottayil temple history, Kottayil temple Muchukunnu, Kottayil Shiva temple history, Muchukunnu temple history, Kotta-Kovilakam heritage, Kottayil kulam history, Kozhikode temple history, ancient temple Kerala, Bhadrakali temple Muchukunnu, കൊട്ടയിൽ ക്ഷേത്രം ചരിത്രം, കൊട്ടയിൽ ക്ഷേത്രം മുച്ചുകുന്ന്, മുച്ചുകുന്ന് ക്ഷേത്രം, കേരള ക്ഷേത്രം ചരിത്രം"
      />
      <PageBanner title="About the Temple" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{
              borderRadius: '28px', overflow: 'hidden', mb: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #78350f 0%, #b45309 40%, #f59e0b 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  }}>
                    <TempleHinduIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' }, color: '#fff', lineHeight: 1.15, mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
                      Muchukunnu Sri Kotta-Kovilakam
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
                      A Spiritually Blessed Tapovana Temple since the Dvapara Yuga
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.55)', lineHeight: 1.9, maxWidth: 560, mb: 2.5 }}>
                  Nestled within ten acres of sacred forest at Muchukunnu near Koyilandy, this ancient Tapovana temple traces its divine origin to the Dvapara Yuga. A place where silence speaks and nature worships.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip icon={<ForestIcon sx={{ fontSize: '16px !important', color: '#f59e0b !important' }} />} label="Sacred Forest" sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#78350f', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', '& .MuiChip-icon': { ml: '8px' } }} />
                  <Chip icon={<SelfImprovementIcon sx={{ fontSize: '16px !important', color: '#fcd34d !important' }} />} label="Tapovana Heritage" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)', '& .MuiChip-icon': { ml: '8px' } }} />
                </Box>
              </Box>
            </Box>

            {/* QUICK FACTS */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {quickFacts.map((f) => (
                <Grid item xs={6} sm={3} key={f.label}>
                  <Box sx={{ bgcolor: '#fff', borderRadius: '20px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', textAlign: 'center', transition: 'all 400ms cubic-bezier(0.25,0.46,0.45,0.94)', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 16px 48px rgba(0,0,0,0.08)' } }}>
                    <Box sx={{ background: `linear-gradient(135deg, ${f.gradient[0]}, ${f.gradient[1]})`, py: 1.5, px: 1 }}>
                      <Typography sx={{ fontWeight: 900, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>{f.label}</Typography>
                    </Box>
                    <Box sx={{ py: 1.2, px: 1 }}>
                      <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 600 }}>{f.sub}</Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* SECTION CARDS */}
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              {sections.map((s) => {
                const Icon = s.icon;
                const [gS, gE] = s.gradient;
                return (
                  <Grid item xs={12} sm={6} key={s.title}>
                    <Box
                      component={RouterLink}
                      to={s.to}
                      sx={{
                        display: 'block', textDecoration: 'none',
                        bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.06)',
                        boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                        transition: 'all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        height: '100%',
                        '&:hover': { transform: 'translateY(-6px)', boxShadow: `0 20px 60px rgba(0,0,0,0.1), 0 0 0 1px ${gS}15`, '& .hArrow': { transform: 'translateX(4px)' } },
                      }}
                    >
                      <Box sx={{ background: `linear-gradient(135deg, ${gS}, ${gE})`, px: 2.5, py: 2, position: 'relative', overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', top: -15, right: -15, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                          <Box sx={{ width: 42, height: 42, borderRadius: '14px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
                            <Icon sx={{ fontSize: 22, color: '#fff' }} />
                          </Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#fff' }}>{s.title}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ px: 2.5, py: 2 }}>
                        <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.7, mb: 1.5 }}>
                          {s.desc}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: gS }}>Explore</Typography>
                          <ArrowForwardIcon className="hArrow" sx={{ fontSize: 14, color: gS, transition: 'transform 300ms ease' }} />
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {/* VISITOR INFO + KEY DEITIES */}
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', height: '100%' }}>
                  <Box sx={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', px: 2.5, py: 2, position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', top: -15, right: -15, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                      <Box sx={{ width: 38, height: 38, borderRadius: '12px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <SpaIcon sx={{ fontSize: 19, color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#fff' }}>Temple Essence</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ p: 2.5 }}>
                    {[
                      'Serene Tapovana atmosphere within ancient fort walls',
                      'Sacred silence — no cultural performances or electric lights',
                      'Devotional singing for inner peace is a cherished tradition',
                      'Connected to the revered Kottiyoor temple tradition',
                    ].map((t, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.1, borderBottom: i < 3 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '4px', background: 'linear-gradient(135deg, #22c55e, #16a34a)', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 500 }}>{t}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', height: '100%' }}>
                  <Box sx={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', px: 2.5, py: 2, position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', top: -15, right: -15, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                      <Box sx={{ width: 38, height: 38, borderRadius: '12px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <AutoAwesomeIcon sx={{ fontSize: 19, color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#fff' }}>Key Deities</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ p: 2.5 }}>
                    {[
                      { name: 'Parameshwara', role: 'Lord Shiva — principal self-manifested deity', gradient: ['#8b5cf6', '#7c3aed'] },
                      { name: 'Parvati Devi', role: 'Goddess Parvati — divine consort of Shiva', gradient: ['#f59e0b', '#d97706'] },
                      { name: 'Subrahmanya', role: 'Son of Shiva — worshipped with deep reverence', gradient: ['#ef4444', '#dc2626'] },
                      { name: 'Ganapathy', role: 'Lord Ganesha — invoked at the start of all rituals', gradient: ['#22c55e', '#16a34a'] },
                    ].map((d, i) => (
                      <Box key={d.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.1, borderBottom: i < 3 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
                        <Box sx={{ width: 34, height: 34, borderRadius: '10px', background: `linear-gradient(135deg, ${d.gradient[0]}, ${d.gradient[1]})`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 4px 12px ${d.gradient[0]}25` }}>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 900, color: '#fff' }}>{d.name.charAt(0)}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>{d.name}</Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>{d.role}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* BOTTOM FOOTER BLOCK */}
            <Box sx={{
              background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
              borderRadius: '24px', p: { xs: 3, sm: 3.5 }, position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.05)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}12 0%, transparent 70%)` }} />
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${GOLD}, #b8941f)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 20px ${GOLD}30` }}>
                    <VolunteerActivismIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', lineHeight: 1.2 }}>Support the Temple</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>Preserve heritage for future generations</Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.7, mb: 2.5 }}>
                  Your generous contributions help preserve our heritage, support daily annadanam, and maintain the temple for future generations of devotees.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Make a Donation', to: '/donate', gradient: ['#f59e0b', '#d97706'] },
                    { label: 'Book a Pooja', to: '/poojas', gradient: ['#8b5cf6', '#7c3aed'] },
                    { label: 'Visit Gallery', to: '/gallery', gradient: ['#3b82f6', '#2563eb'] },
                  ].map((link) => (
                    <Box key={link.label} component={RouterLink} to={link.to} sx={{ px: 2.5, py: 0.9, borderRadius: '12px', background: `linear-gradient(135deg, ${link.gradient[0]}20, ${link.gradient[1]}10)`, border: `1px solid ${link.gradient[0]}25`, textDecoration: 'none', transition: 'all 300ms ease', '&:hover': { background: `linear-gradient(135deg, ${link.gradient[0]}35, ${link.gradient[1]}20)`, transform: 'translateY(-2px)' } }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: link.gradient[0] }}>{link.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="about" activePath="/about" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
